import React, { ReactNode, useCallback } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { MinimalAppSkeleton } from '@/components/ui/app-skeleton';
import { createScopedLogger } from '@/utils/debug';
import { returnTo } from './returnTo';
import { SessionUnreachable } from './SessionUnreachable';

interface AuthGuardProps {
  children?: ReactNode;
  requireCustomer?: boolean;
}

export const AuthGuard = ({
  children,
  requireCustomer = true
}: AuthGuardProps) => {
  const {
    isAuthenticated,
    isInitialized,
    isLoading,
    isUnreachable,
    activeCustomer,
    user,
  } = useAuth();

  const location = useLocation();

  // Create a logger function that returns the scoped logger object
  const logger = useCallback(() => createScopedLogger(user, 'AuthGuard'), [user]);

  // Helper function to log debug messages using the enhanced logger
  const logDebug = useCallback((message: string, data?: any) => {
    logger().debug(message, data);
  }, [logger]);

  // NOTE: customer resolution now happens entirely in ``AuthProvider`` —
  // it activates the stored ``active_customer`` or fetches the user's
  // ``last_active_customer`` via a SINGLE ``getCustomer(id)`` during init,
  // so this guard no longer needs the full customer list to auto-select.
  // If no active customer could be resolved, we send the user to
  // ``/select-customer`` (the only surface that fetches the full list).

  // If auth is still initializing, show skeleton loading
  if (!isInitialized || isLoading) {
    return <MinimalAppSkeleton />;
  }

  // The backend did not answer the session check, but nothing rejected the
  // session either: offer a retry, never /login (#433 — that bounce read as a
  // logout and stranded the user on the login form with valid tokens).
  if (!isAuthenticated && isUnreachable) {
    return <SessionUnreachable />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    logDebug('User not authenticated, redirecting to login');
    return (
      <Navigate 
        to="/login" 
        state={{ from: returnTo(location) }} 
        replace 
      />
    );
  }

  // If auth requires customer selection and no customer is active
  if (requireCustomer && !activeCustomer) {
    logDebug('Customer required but no active customer, redirecting to select-customer');
    return (
      <Navigate 
        to="/select-customer" 
        state={{ from: returnTo(location) }}
        replace 
      />
    );
  }

  // Render children if provided, otherwise render the Outlet for nested routes
  return <>{children || <Outlet />}</>;
};
